<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  FormDefinition,
  FormElementBase,
  FormElementRule,
  FormElementRuleType,
  LoginAccountRuleCondition,
  LoginAccountRuleType,
  RuleConditionBase,
  RuleConditionType,
  SectionAssignables,
  SysOrgRoleName,
  WorkflowDefinition,
} from "~/api";
import { loginAccountConditionDisplayNames } from "~/utils/enumDisplayNames";
import { showError } from "~/utils/handleAPIError";
import { isLoginAccountBasedCondition } from "~/utils/typeGuards";

export interface EditCondition {
  rule: FormElementRule;
  condition: RuleConditionBase;
}

const emit = defineEmits<{
  (e: "update:rules", value: FormElementRule[]): void;
  (e: "update:editing-condition", value?: EditCondition): void;
}>();

const props = defineProps<{
  form: FormDefinition;
  workflowDefinition: WorkflowDefinition;
  sectionAssignables?: SectionAssignables;
  editingCondition?: EditCondition;
}>();

/** Adding a Rule */
const ruleConditionType = RuleConditionType.LOGIN_ACCOUNT_BASED;

/** the list of form elements selected for a rule to be applied to */
const _applicableElementGuids = ref<string[]>([]);
const applicableElements = computed<FormElementBase[]>(() =>
  props.form.components.filter((c) =>
    _applicableElementGuids.value.includes(c.guid),
  ),
);

/**
 * The role or security group which the rule depends on. Prefixed so one dropdown can
 * offer both: "role:Member" targets the fixed SysOrgRoleName set every org has,
 * "group:<guid>" targets an org-defined security group.
 */
const _conditionGroup = ref<string>();

const rolePrefix = "role:";
const groupPrefix = "group:";

/** Roles and groups, from the same source the section Access tab uses. */
const scopeOptionGroups = computed(() => [
  {
    label: "Roles",
    items:
      props.sectionAssignables?.roles?.map((r) => ({
        value: `${rolePrefix}${r.key}`,
        label: r.value ?? String(r.key),
      })) ?? [],
  },
  {
    label: "Groups",
    items:
      props.sectionAssignables?.groups?.map((g) => ({
        value: `${groupPrefix}${g.key}`,
        label: g.value ?? "",
      })) ?? [],
  },
]);

/** the type of the rule (e.g. hide, readonly) */
const ruleType = ref<FormElementRuleType>();
/** the type of the condition (e.g. status is, status is not, etc.) */
const conditionType = ref<LoginAccountRuleType>(
  Object.values(LoginAccountRuleType)[0],
);

const loginAccountRuleType = computed({
  get: () => conditionType.value,
  set: (value) => {
    conditionType.value = value;
  },
});

/** whether to use "and" or "or" logic for rules with multiple conditions */
const trueIfAny = ref<boolean>(true);

/** available form items */
const formItems = computed<FormElementBase[]>(() =>
  props.form.components.filter((c) => c.guid !== undefined && !c.isArchived),
);

/** 'true' if the form item is not required for the condition */
const isRuleReadyToAdd = computed<boolean>(() =>
  Boolean(
    applicableElements.value &&
      ruleType.value &&
      conditionType.value &&
      _conditionGroup.value,
  ),
);

/**
 * add a rule to the form
 * @param elementQuestionCode the question code of the element to add the rule to
 */
const addElementRuleToForm = (
  elementQuestionCode: string,
  elementGuid: string | undefined,
  newCondition: LoginAccountRuleCondition,
) => {
  if (!isRuleReadyToAdd.value) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields",
    );
    return;
  }
  if (!ruleType.value || !elementQuestionCode) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields 2",
    );
    return;
  }

  if (props.editingCondition) {
    // Find the original rule that contains the condition we're editing
    const originalRule = props.form.rules.find(
      (rule) => rule.guid === props.editingCondition?.rule.guid,
    );

    if (originalRule) {
      const updatedRule = {
        ...originalRule,
        elementQuestionCode: elementQuestionCode,
        elementGuid: elementGuid ?? "",
        ruleType: ruleType.value,
        trueIfAny: trueIfAny.value,
        conditions: [
          ...originalRule.conditions.filter(
            (c) => c.guid !== props.editingCondition?.condition.guid,
          ),
          newCondition,
        ],
      };

      emit("update:rules", [
        ...props.form.rules.filter((r) => r !== originalRule),
        updatedRule,
      ]);
      emit("update:editing-condition", undefined);
      return;
    }
  }

  // check if the rule already exists
  const existingRule = props.form.rules.find(
    (rule) =>
      rule.elementQuestionCode === elementQuestionCode &&
      rule.ruleType === ruleType.value,
  );

  if (existingRule) {
    // Add the condition to the existing rule
    const updatedExistingRule = {
      ...existingRule,
      conditions: [...existingRule.conditions, newCondition],
      trueIfAny: trueIfAny.value,
    };
    emit("update:rules", [
      ...props.form.rules.filter((r) => r !== existingRule),
      updatedExistingRule,
    ]);
    return;
  }

  // otherwise, create a new rule
  const newRule: FormElementRule = {
    // @ts-expect-error: Force the server to generate the Guid
    guid: undefined,
    elementQuestionCode: elementQuestionCode,
    elementGuid: elementGuid ?? "",
    ruleType: ruleType.value,
    trueIfAny: trueIfAny.value,
    conditions: [newCondition],
  };

  emit("update:rules", [...props.form.rules, newRule]);
};

const resetFormFields = () => {
  conditionType.value = LoginAccountRuleType.USER_BELONGS_TO;
  _conditionGroup.value = undefined;
  ruleType.value = undefined;
  _applicableElementGuids.value = [];
  trueIfAny.value = true;
};

/**
 * iterates through the list of question codes selected and adds the rule to each element
 */
const handleAddRule = () => {
  if (!isRuleReadyToAdd.value || !conditionType.value) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields",
    );
    return;
  }

  const scope = _conditionGroup.value ?? "";
  const isRole = scope.startsWith(rolePrefix);
  const newCondition: LoginAccountRuleCondition = {
    // @ts-expect-error: Force the server to generate the Guid
    guid: undefined,
    conditionType: ruleConditionType,
    type: conditionType.value,
    // Exactly one of these is set; the other stays undefined so the server stores null.
    role: isRole
      ? (scope.slice(rolePrefix.length) as SysOrgRoleName)
      : undefined,
    orgGroupGuid: isRole ? undefined : scope.slice(groupPrefix.length),
  };

  // iterate through the list of element guids selected and add the rule to each element
  applicableElements.value.forEach((el) =>
    addElementRuleToForm(el.questionCode, el.guid, newCondition),
  );
  resetFormFields();
};

watch(
  () => props.editingCondition,
  () => {
    if (
      props.editingCondition &&
      isLoginAccountBasedCondition(props.editingCondition.condition)
    ) {
      const editing = props.editingCondition.condition;
      if (editing.role) {
        _conditionGroup.value = `${rolePrefix}${editing.role}`;
      } else if (editing.orgGroupGuid) {
        _conditionGroup.value = `${groupPrefix}${editing.orgGroupGuid}`;
      } else {
        _conditionGroup.value = undefined;
      }
      conditionType.value = props.editingCondition.condition.type;
      ruleType.value = props.editingCondition.rule.ruleType;
      _applicableElementGuids.value = [props.editingCondition.rule.elementGuid];
      trueIfAny.value = props.editingCondition.rule.trueIfAny;
    } else {
      conditionType.value = LoginAccountRuleType.USER_BELONGS_TO;
      _conditionGroup.value = undefined;
      ruleType.value = undefined;
      _applicableElementGuids.value = [];
      trueIfAny.value = true;
    }
  },
);
</script>

<template>
  <div class="rule-editor">
    <div>If User</div>
    <el-select
      v-model="loginAccountRuleType"
      size="small"
      placeholder="Condition"
      clearable
      style="max-width: 200px"
      filterable
    >
      <el-option
        v-for="condition in Object.values(LoginAccountRuleType)"
        :key="condition"
        :label="loginAccountConditionDisplayNames[condition]"
        :value="condition"
      >
      </el-option>
    </el-select>

    <el-select
      v-model="_conditionGroup"
      size="small"
      placeholder="Role / Security Group"
      clearable
      filterable
    >
      <el-option-group
        v-for="optionGroup in scopeOptionGroups"
        :key="optionGroup.label"
        :label="optionGroup.label"
      >
        <el-option
          v-for="item in optionGroup.items"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        >
        </el-option>
      </el-option-group>
    </el-select>

    <span> Set </span>
    <el-select
      v-if="!editingCondition"
      v-model="_applicableElementGuids"
      size="small"
      placeholder="Form Item(s)"
      multiple
      collapse-tags
      collapse-tags-tooltip
      filterable
      clearable
    >
      <el-option
        v-for="item in formItems"
        :key="item.guid"
        :label="item.questionCode"
        :value="item.guid"
      >
      </el-option>
    </el-select>
    <el-select
      v-if="editingCondition"
      v-model="_applicableElementGuids"
      size="small"
      placeholder="Form Item(s)"
      collapse-tags
      collapse-tags-tooltip
      filterable
      clearable
    >
      <el-option
        v-for="item in formItems"
        :key="item.guid"
        :label="item.questionCode"
        :value="item.guid"
      >
      </el-option>
    </el-select>

    <span> to </span>

    <el-select
      v-model="ruleType"
      size="small"
      placeholder="Rule Type"
      filterable
      clearable
    >
      <el-option
        v-for="rt in Object.values(FormElementRuleType)"
        :key="rt"
        :label="rt"
        :value="rt"
      >
      </el-option>
    </el-select>

    <el-checkbox v-model="trueIfAny" size="small" label="True if any?" />
    <el-button
      v-if="!editingCondition"
      :disabled="!isRuleReadyToAdd"
      type="primary"
      @click="handleAddRule"
    >
      Add
    </el-button>
    <div v-else style="display: flex">
      <el-button
        type="primary"
        :disabled="!isRuleReadyToAdd"
        @click="handleAddRule"
      >
        Update
      </el-button>

      <el-button
        type="danger"
        plain
        @click="
          () => {
            emit('update:editing-condition', undefined);
            resetFormFields();
          }
        "
      >
        Cancel
      </el-button>
    </div>
  </div>
</template>

<style lang="less" scoped>
.rule-editor {
  display: grid;
  grid-template-columns:
    max-content repeat(2, auto)
    max-content auto max-content auto repeat(2, max-content);
  gap: 20px;
  align-items: center;
  .answer {
    height: fit-content;
  }
}
</style>
